/** @jsx jsx */
import { jsx } from '@emotion/react'
import {
    AllEvents,
    CardBreakpointRatios,
    CardItem,
    createCollectionAvailableEvent,
    FixedRatio,
} from '@news-mono/web-common'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import React from 'react'
import { CommonCardProps } from '../../cards/CardItem.Props'
import {
    DisplayOnBreakpoint,
    StyledGridItem,
    StyledGridWrapper,
    StyledHeader,
    StyledRomeo,
} from './Romeo.styled'
import { TimestampType } from '../../content/CardTimestamp/CardTimestamp'
import {
    ResponsiveContainer,
    ResponsivePictureSizes,
} from '../../content/Picture/responsive'
import { CardSwitcher } from '../../__helpers/CardSwitcher'
import { ImpressionAvailable } from '../../__helpers/impression-available-helper'
import { useProduct } from '../../__product/useProduct'
import { ThemeMargins } from '../../__styling/settings/metrics'
import { invertMaybeLoadedItems } from '../helpers/loading'
import {
    PNSectionHeader,
    PNSectionHeaderProps,
} from '../../nextgen-news/perthnow/PNSectionHeader'
import {
    ImageMode,
    HeaderSectionTagType,
    PNArticleCard,
    PNFontScales,
    KickerMode,
    PNArticleCardProps,
} from '../../nextgen-news/perthnow/cards/PNArticleCard'
import { TeaserMode } from '../../cards/CardText/CardTeaser'

export interface RomeoProps extends CommonCardProps, ResponsiveContainer {
    className?: string
    sectionHeader?: PNSectionHeaderProps
    showLargeCard?: boolean
    fixedRatios?: FixedRatio | FixedRatio[] | CardBreakpointRatios
    imageWidths?: ResponsivePictureSizes
    isTimestamped?: boolean
    verticalSpacing?: keyof ThemeMargins
    onEvent: (event: AllEvents) => void
    disableImageLazyLoad?: boolean
    items: MaybeLoaded<CardItem[]>
    expectedCards: number
    noHorizontalDividers?: boolean
    topicLevel?: 'parent'
    hideTopDivider?: boolean
    hideLastDivider?: boolean
    cardSpacing?: number
    hideOnMobile?: boolean
    fontScale?: PNFontScales
    teaserFontScale?: PNFontScales
    headerFontScale?: PNFontScales
    imageMode?: ImageMode
    teaserMode?: TeaserMode
    kickerMode?: KickerMode
    showAuthor?: boolean
    showCardTopic?: boolean
    hideCardFooter?: boolean
    horizontalDividerColor?: string
    displayOnBreakpoint?: DisplayOnBreakpoint
    marginTop?: number
    marginBottom?: number
    headerSectionTag?: HeaderSectionTagType
    centreOnTablet?: boolean
    topBottomDividerColorOverride?: string
    headerFontOverride?: PNArticleCardProps['headerFontOverride']
    imageSizeOverride?: PNArticleCardProps['imageSizeOverride']
    itemClickEventHandler?: (
        e: React.MouseEvent<HTMLElement>,
        item: MaybeLoaded<CardItem>,
        cardNumber: number,
    ) => void
}

export const Romeo: React.FC<RomeoProps> = (props) => {
    const {
        className,
        sectionHeader,
        onEvent,
        showLargeCard,
        expectedCards,
        noHorizontalDividers,
        hideTopDivider,
        hideLastDivider,
        topBottomDividerColorOverride,
        horizontalDividerColor,
        cardSpacing = 24,
        displayOnBreakpoint,
        marginTop,
        marginBottom,
        centreOnTablet,
        itemClickEventHandler,
    } = props
    const product = useProduct()
    const items = invertMaybeLoadedItems(props.items, expectedCards)

    const numItems = items

    const renderRomeo = () => (
        <ImpressionAvailable
            loading={!props.items.loaded}
            available={() => {
                if (!props.items.loaded) {
                    console.warn(
                        'Available should never be called when loading is true',
                    )
                    return
                }
                onEvent(
                    createCollectionAvailableEvent(
                        props.items.result,
                        'Romeo',
                        product,
                        onEvent,
                    ),
                )
            }}
        >
            {(ref) => (
                <StyledRomeo
                    ref={ref}
                    className={className}
                    displayOnBreakpoint={displayOnBreakpoint}
                    hideTopDivider={hideTopDivider ?? true}
                    cardSpacing={cardSpacing}
                    marginTop={marginTop}
                    marginBottom={marginBottom}
                    centreOnTablet={centreOnTablet}
                >
                    {sectionHeader && items && (
                        <StyledHeader>
                            <PNSectionHeader
                                {...sectionHeader}
                                onEvent={onEvent}
                            />
                        </StyledHeader>
                    )}
                    {numItems.length > 0 && (
                        <StyledGridWrapper>
                            {numItems.map((item, index) => {
                                return (
                                    <StyledGridItem
                                        onClick={(event) =>
                                            itemClickEventHandler &&
                                            itemClickEventHandler(
                                                event,
                                                item,
                                                index,
                                            )
                                        }
                                        key={index}
                                        hideTopDivider={hideTopDivider ?? true}
                                        horizontalDividerColor={
                                            horizontalDividerColor
                                        }
                                        noHorizontalDividers={
                                            noHorizontalDividers === true
                                        }
                                        hideLastDivider={hideLastDivider}
                                        cardSpacing={cardSpacing}
                                        topBottomDividerColorOverride={
                                            topBottomDividerColorOverride
                                        }
                                    >
                                        {determineLandscapeCardToRender(
                                            item,
                                            index + (showLargeCard ? 2 : 1),
                                            props,
                                        )}
                                    </StyledGridItem>
                                )
                            })}
                        </StyledGridWrapper>
                    )}
                </StyledRomeo>
            )}
        </ImpressionAvailable>
    )

    return renderRomeo()
}
Romeo.displayName = 'Romeo'

export function renderLandscape(
    item: MaybeLoaded<CardItem>,
    cardNumber: number,
    props: RomeoProps,
) {
    const onEvent = props.onEvent

    return (
        <CardSwitcher
            onEvent={onEvent}
            item={item}
            cardNumber={cardNumber}
            cardContext="Romeo-landscape"
            publicationCard={(publicationItem) => (
                <PNArticleCard
                    item={publicationItem}
                    onEvent={props.onEvent}
                    cardNumber={cardNumber}
                    cardType={'horizontal'}
                    teaserMode={props.teaserMode || 'hidden'}
                    fontScale={props.fontScale || 'S'}
                    teaserFontScale={
                        props.teaserFontScale || props.fontScale || 'S'
                    }
                    headerFontScale={
                        props.headerFontScale || props.fontScale || 'S'
                    }
                    hideByline={props.hideByline}
                    hideFooter={props.hideCardFooter}
                    fixedRatio={props.fixedRatios || ['4:3', '16:9']}
                    disableImageLazyLoad={props.disableImageLazyLoad}
                    imageMode={props.imageMode}
                    showAuthor={props.showAuthor}
                    showTopic={props.showCardTopic}
                    headerSectionTag={props.headerSectionTag}
                    kickerMode={props.kickerMode}
                    headerFontOverride={props.headerFontOverride}
                    imageSizeOverride={props.imageSizeOverride}
                    isTimestamped={props.isTimestamped}
                />
            )}
        />
    )
}

function determineLandscapeCardToRender(
    item: MaybeLoaded<CardItem>,
    cardNumber: number,
    props: RomeoProps,
) {
    return renderLandscape(item, cardNumber, props)
}
